/**
 * /sign-up — dedicated account-creation route.
 *
 * Renders SignInGate in "signup" mode so users who navigate directly to
 * /sign-up land on the registration form without having to click the
 * mode-switch affordance. On session established, navigates to /app.
 *
 * Gate behaviour:
 *   - Invite-token field is always rendered — the server enforces the gate
 *     (`WAITLIST_REQUIRED`) rather than the client, so the client-side
 *     presentation is identical on dev and prod. On dev the server accepts
 *     any non-empty value; on prod an invalid token returns ACCESS_DENIED.
 *   - Email verification (`REQUIRE_EMAIL_VERIFICATION`) is also server-side.
 *     On dev the session cookie is issued immediately after signup; on prod
 *     a verify-your-email state is returned instead. Neither is surfaced
 *     client-side by this route — the server response drives post-signup UX.
 *
 * Architecture refs:
 *   architecture.md §4.7  — invite-token gate (WAITLIST_REQUIRED, server-side)
 *   architecture.md §4.11 — email verification gate (REQUIRE_EMAIL_VERIFICATION)
 *
 * Design refs:
 *   design.md §2  — warm neutrals, hairlines, one accent
 *   design.md §4  — token register (space, radius, colour)
 *   branding.md §5 — quiet, precise voice; no exclamations; no banned words
 *
 * CLAUDE.md Rule 7 — verified at 375 px / 768 px / 1280 px.
 * CLAUDE.md Rule 8 — all error paths in SignInGate surface inline messages.
 */

import { useNavigate, useSearchParams } from "react-router";
import { useCallback } from "react";
import { SignInGate } from "~/features/auth/SignInGate";
import { resolvePostAuthRedirect } from "~/features/groups/pendingGroupJoin";
import { resolvePendingFriendInviteRedirect } from "~/features/friends/pendingFriendInvite";
import { safeReturnTo } from "./sign-in";
import "@proof/ui/tokens";

export default function SignUpRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Thread `returnTo` through sign-UP exactly like sign-IN: a brand-new user who
  // follows a group invite link lands on /sign-in?returnTo=/app/join/<token>,
  // switches to "Create one", and must return to the join page after signing up
  // (not the generic /app). Same open-redirect guard (safeReturnTo).
  const returnTo = safeReturnTo(searchParams.get("returnTo"));

  const handleSessionEstablished = useCallback(() => {
    // A pending group invite (signup-via-group-invite path) takes precedence
    // over returnTo and routes to the join page with auto-file (operator Q1).
    // Else, a pending FRIEND invite (B-WP11c) routes to the friend-invite landing
    // with auto-accept. Group takes precedence when both somehow exist.
    const afterGroup = resolvePostAuthRedirect(returnTo);
    const dest =
      afterGroup === returnTo ? resolvePendingFriendInviteRedirect(returnTo) : afterGroup;
    void navigate(dest, { replace: true });
  }, [navigate, returnTo]);

  return <SignInGate initialMode="signup" onSessionEstablished={handleSessionEstablished} />;
}
